// skipcq: JS-W1028, JS-0128
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ref,
  listAll,
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
} from 'firebase/storage';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { storage, db } from '../utils/firebase';
import { SPLAT_DEFINITIONS_COLLECTION } from '../utils/firestoreCollections';
import {
  SPLAT_STORAGE_FOLDER,
  sanitizeSplatImageFilename,
  validateSplatImageFile,
} from '../pages/splats/splatImageUpload';

/**
 * `listAll` yields StorageReference values — use `getDownloadURL(item)` directly.
 * Do not use `item.ref` (not a child ref; breaks URL resolution).
 */
async function getDownloadUrlForListedItem(item) {
  const fullPath =
    typeof item?.fullPath === 'string' ? item.fullPath : null;

  const attempts = [
    { label: 'getDownloadURL(list item)', run: () => getDownloadURL(item) },
    ...(fullPath
      ? [
          {
            label: `getDownloadURL(ref(storage, "${fullPath}"))`,
            run: () => getDownloadURL(ref(storage, fullPath)),
          },
        ]
      : []),
  ];

  let lastErr = null;
  for (const { label, run } of attempts) {
    try {
      const url = await run();
      if (url) {
        return { url };
      }
    } catch (err) {
      lastErr = err;
      const code = err?.code ?? 'unknown';
      const message = err?.message ?? String(err);
      // eslint-disable-next-line no-console
      console.error(
        `[useSplatStorageImages] getDownloadURL failed (${label})`,
        fullPath ?? item?.name,
        code,
        message,
        err,
      );
    }
  }

  const code = lastErr?.code ?? 'unknown';
  const message = lastErr?.message ?? 'Unknown error';
  return {
    url: null,
    resolveError: `${code}: ${message}`,
  };
}

/**
 * Lists splat PNGs under Storage `splats/`, resolves preview URLs, uploads with validation,
 * and deletes only when no splat definition references the filename.
 */
function useSplatStorageImages() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filesRef = useRef(files);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const folderRef = ref(storage, SPLAT_STORAGE_FOLDER);
      const res = await listAll(folderRef);
      const items = await Promise.all(
        res.items.map(async (item) => {
          const resolved = await getDownloadUrlForListedItem(item);
          if (resolved.url) {
            return {
              name: item.name,
              url: resolved.url,
              urlError: false,
              resolveError: null,
            };
          }
          return {
            name: item.name,
            url: null,
            urlError: true,
            resolveError: resolved.resolveError ?? null,
          };
        }),
      );
      items.sort((a, b) => a.name.localeCompare(b.name));
      setFiles(items);
    } catch (err) {
      const code = err?.code ?? 'unknown';
      const message = err?.message ?? String(err);
      // eslint-disable-next-line no-console
      console.error(
        '[useSplatStorageImages] Failed to list or load splat images from storage.',
        code,
        message,
        err,
      );
      setError('Failed to load images from storage.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const fileNames = useMemo(() => files.map((f) => f.name), [files]);

  const imageUrlByName = useMemo(() => {
    const map = {};
    for (const f of files) {
      if (f.url) {
        map[f.name] = f.url;
      }
    }
    return map;
  }, [files]);

  const uploadFile = useCallback(
    async (file, { onProgress, overwrite = false } = {}) => {
      const validation = await validateSplatImageFile(file);
      if (!validation.ok) {
        return { ok: false, message: validation.message };
      }

      const name = sanitizeSplatImageFilename(file.name);
      if (!name) {
        return {
          ok: false,
          message:
            'Invalid file name. Use only letters, numbers, dots, dashes, underscores, end with .png.',
        };
      }

      const exists = filesRef.current.some((f) => f.name === name);
      if (exists && !overwrite) {
        return {
          ok: false,
          code: 'DUPLICATE',
          message: `An image named "${name}" already exists.`,
        };
      }

      const storageRef = ref(storage, `${SPLAT_STORAGE_FOLDER}/${name}`);

      return new Promise((resolve) => {
        const task = uploadBytesResumable(storageRef, file, {
          contentType: 'image/png',
          cacheControl: 'public, max-age=31536000',
        });
        task.on(
          'state_changed',
          (snap) => {
            const pct = Math.round(
              (snap.bytesTransferred / snap.totalBytes) * 100,
            );
            onProgress?.(pct);
          },
          (err) => {
            resolve({
              ok: false,
              message: err?.message || 'Upload failed. Please try again.',
            });
          },
          async () => {
            try {
              await refresh();
              onProgress?.(100);
              resolve({ ok: true, name });
            } catch (e) {
              resolve({
                ok: false,
                message: e?.message || 'Upload finished but refresh failed.',
              });
            }
          },
        );
      });
    },
    [refresh],
  );

  const deleteImageIfUnused = useCallback(
    async (objectName) => {
      const trimmed = (objectName ?? '').trim();
      if (!trimmed) {
        return { ok: false, message: 'Missing image name.' };
      }

      try {
        const definitionsByImageQuery = query(
          collection(db, SPLAT_DEFINITIONS_COLLECTION),
          where('image', '==', trimmed),
          limit(25),
        );
        const snap = await getDocs(definitionsByImageQuery);
        if (!snap.empty) {
          const referencing = snap.docs.map((docSnap) => ({
            id: docSnap.id,
            name: docSnap.data().name || docSnap.id,
          }));
          return { ok: false, referencing };
        }

        const storageRef = ref(storage, `${SPLAT_STORAGE_FOLDER}/${trimmed}`);
        await deleteObject(storageRef);
        await refresh();
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          message: e?.message || 'Could not delete image.',
        };
      }
    },
    [refresh],
  );

  return {
    files,
    fileNames,
    imageUrlByName,
    loading,
    error,
    refresh,
    uploadFile,
    deleteImageIfUnused,
  };
}

export default useSplatStorageImages;
